"use client";
import {
	faBook,
	faBookOpen,
	faChessKnight,
	faFeather,
	faInfinity,
	faLink,
	faScroll,
	faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";

const TarragonPage = () => {
	return (
		<main className="bg-black text-white">
			{/* Hero Section */}
			<section
				className="relative py-32 bg-cover bg-center"
				style={{ backgroundImage: "url('/images/sistemas/tarragon_Header.webp')" }}
			>
				<div className="absolute inset-0 bg-black/60"></div>
				<div className="container mx-auto px-4 max-w-4xl relative z-10">
					<motion.h1
						className="font-amarante text-5xl md:text-7xl mb-6 text-center text-white"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
					>
						<span className="headerFont inline-block pb-2 border-b-2 border-red-700">DRAGON</span>
					</motion.h1>

					<motion.p
						className="text-xl text-gray-300 max-w-3xl mx-auto text-center mb-12"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.3, duration: 0.6 }}
					>
						<strong className="">
							Menos reglas, más libertad y diversión. El juego de rol DRAGON utiliza las reglas de Agon RPG,
							pero en un ambiente de fantasía similar al utilizado en Dragones y Mazmorras. Este sistema se
							enfoca en la narrativa, lo que permite partidas rápidas y de alta intensidad. Además, las reglas
							simples hacen que sea fácil de aprender para cualquier persona. La única limitación en la
							creación de personajes es tu imaginación.
						</strong>
					</motion.p>
				</div>
			</section>

			{/* Main Content Section */}
			<section className="py-16 bg-black">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.6 }}
						className="prose prose-invert max-w-none"
					>
						{/* Historical Context and Evolution */}
						<div className="bg-gray-900 rounded-lg p-8 mb-12 border-2 border-red-900/50">
							<h2 className="text-3xl font-amarante headerFont mb-6" style={{ textAlign: "left" }}>
								<FontAwesomeIcon icon={faFeather} className="mr-3 text-red-500" />
								Los juegos de rol del siglo XXI
							</h2>
							<p className="text-gray-300 mb-4">
								Powered by the Apocalypse (PbtA) es un sistema de juegos de rol que fue desarrollado por el
								diseñador de juegos de rol, Vincent Baker, en 2010 con su juego «Apocalypse World». Desde
								entonces, PbtA ha ganado popularidad entre la comunidad de jugadores de rol, y ha sido
								utilizado como base para una variedad de juegos de rol, incluyendo «Dungeon World», «Blades in
								the Dark», y «Agon».
							</p>
							<p className="text-gray-300">
								La popularidad de PbtA se debe en gran parte a su enfoque en la narrativa y la simplicidad de
								sus reglas, lo que lo hace accesible y fácil de jugar para jugadores nuevos y experimentados.
								Además, PbtA permite a los jugadores crear personajes más complejos y dinámicos que
								simplemente siguen un conjunto de reglas establecidas, lo que les permite una mayor libertad
								en la creación de personajes y un juego más colaborativo.
							</p>
						</div>

						{/* Game Characteristics */}
						<div className="grid md:grid-cols-2 gap-8 mb-12">
							<div className="bg-gray-800 rounded-lg p-6">
								<h3 className="text-2xl font-amarante text-red-400 mb-4">
									<FontAwesomeIcon icon={faChessKnight} className="mr-3 text-red-500" />
									Filosofía del Juego
								</h3>
								<p className="text-gray-300">
									DRAGON se enfoca en la narrativa y la experiencia del jugador, mientras que Dragones &
									Mazmorras se centra en la estrategia y la mecánica del juego. Los jugadores que valoran la
									narrativa encuentran que DRAGON es una opción más satisfactoria.
								</p>
							</div>
							<div className="bg-gray-800 rounded-lg p-6">
								<h3 className="text-2xl font-amarante text-red-400 mb-4">
									<FontAwesomeIcon icon={faBook} className="mr-3 text-red-500" />
									Reglas Mínimas
								</h3>
								<p className="text-gray-300">
									Con unas reglas aún más simples y accesibles que Dragones & Mazmorras, DRAGON es más fácil
									de aprender y jugar para jugadores nuevos o menos experimentados. La narrativa es un aspecto
									clave, ya que permite a los jugadores sumergirse en un mundo de fantasía y vivir aventuras
									únicas e intensas.
								</p>
							</div>
						</div>

						{/* Detailed Exploration */}
						<div className="space-y-8">
							<div>
								<h3
									className="text-2xl font-amarante headerFont text-left mb-4"
									style={{ textAlign: "left" }}
								>
									<FontAwesomeIcon icon={faInfinity} className="mr-3 text-red-500" />
									Fantasía Heroica sin límites
								</h3>
								<p className="text-gray-300 mb-4">
									Las aventuras de DRAGON pueden tener lugar en cualquier ambientación, y tomar elementos de
									cualquier historia. ¿Qué es lo que buscas? ¿Una fantasía clásica con elfos y enanos? ¿Islas
									que flotan en el aire, cada una con su propias leyes y reglas sociales? ¿Una civilización de
									antiguos robots mineros que han tomado el control y exclavizado a sus antiguos dueños? En
									todos los aspectos de DRAGON, tu imaginación es el único límite.
								</p>
							</div>

							{/* Personajes */}
							<div className="bg-gray-900 rounded-lg p-8">
								<h3 className="text-2xl font-amarante text-red-400 mb-6">
									<FontAwesomeIcon icon={faUsers} className="mr-3 text-red-500" />
									Personajes Infinitos
								</h3>
								<p className="text-gray-300 mb-4">
									Para crear un personaje en DRAGON, dale un nombre, una descripción, y unas habilidades, y ya
									estás listo para jugar. Aquí tienes algunos personajes que han creado nuestros jugadores:
								</p>
								<div className="grid md:grid-cols-2 gap-6">
									<div>
										<ul className="list-disc pl-5 text-gray-300 space-y-2">
											<li>Un hombre cangrejo que utiliza sus pinzas para crear un ataque súper sónico</li>
											<li>Un dragón que se disfraza como un humano para mezclarse con la sociedad</li>
											<li>Un murciélago gigante que puede controlar el viento y la tormenta</li>
											<li>
												Una medusa flotante que puede convertir a sus enemigos en piedra con sus tentáculos
											</li>
										</ul>
									</div>
									<div>
										<ul className="list-disc pl-5 text-gray-300 space-y-2">
											<li>Un robot humanoide que lucha con una espada laser y escudos energéticos</li>
											<li>
												Un licántropo vampiro que utiliza sus habilidades sobrenaturales para sobrevivir en la
												oscuridad
											</li>
											<li>Un burócrata que no sabe muy bien cómo se ha metido en esta aventura</li>
										</ul>
									</div>
								</div>
							</div>

							{/* Para quién es DRAGON */}
							<div>
								<h3
									className="text-2xl font-amarante headerFont text-red-500 mb-4"
									style={{ textAlign: "left" }}
								>
									<FontAwesomeIcon icon={faScroll} className="mr-3 text-red-500" />
									¿Qué tipo de jugador es el que mejor se adapta a DRAGON?
								</h3>
								<div className="bg-gray-800 rounded-lg p-6 text-gray-300">
									<p className="mb-4">DRAGON es una gran opción para:</p>
									<ul className="list-disc pl-5 space-y-2">
										<li>
											<strong>Jugadores nuevos</strong> que son nuevos en los juegos de rol o se sienten
											intimidados por conjuntos de reglas más complejos. La mecánica sencilla de DRAGON lo
											hace fácil de aprender, y el enfoque en la narrativa en lugar de las mecánicas significa
											que los jugadores no necesitan memorizar una gran cantidad de reglas para disfrutar del
											juego.
										</li>
										<li>
											<strong>Jugadores experimentados</strong> que buscan un cambio de ritmo con respecto a
											los juegos de rol tradicionales. El énfasis del sistema en la colaboración y las
											opciones infinitas permite historias más dinámicas e inesperadas, lo que puede ser un
											cambio refrescante con respecto a los juegos que dependen más de escenarios y mecánicas
											pre-escritas.
										</li>
									</ul>
								</div>
							</div>

							{/* Aventuras */}
							<div className="bg-gray-900 rounded-lg p-8">
								<h3 className="text-2xl font-amarante text-red-400 mb-6">
									¿Qué aventuras puedes encontrar en DRAGON?
								</h3>
								<p className="text-gray-300 mb-4">
									Todas nuestras aventuras pueden ser adaptadas al sistema DRAGON, ¡por lo que aquí tampoco
									hay límites! Dragones, vampiros, naves espaciales, viajeros en el tiempo… Todos los mundos
									están a tu alcance, ahora más fácil que nunca.
								</p>
							</div>

							{/* Getting Started */}
							<div>
								<h3
									className="text-2xl font-amarante headerFont text-red-500 mb-4"
									style={{ textAlign: "left" }}
								>
									<FontAwesomeIcon icon={faBookOpen} className="mr-3 text-red-500" />
									¡Me encanta! ¿Por dónde empiezo?
								</h3>
								<div className="bg-gray-800 rounded-lg p-6 text-gray-300">
									<p className="mb-4">Para iniciarse, te recomendamos:</p>
									<ul className="list-disc pl-5 space-y-2">
										<li>
											<strong>DRAGON es un sistema homebrew</strong> que adapta las reglas de Agon; puedes
											encontrar los manuales originales de Agon en su página web, incluyendo el Player Kit
											gratuito en PDF que contiene las reglas básicas para embarcarte en tu propia aventura.
											<a
												href="https://www.evilhat.com/home/agon/"
												target="_blank"
												rel="noopener noreferrer"
												className="text-red-400 ml-2 hover:underline"
											>
												<FontAwesomeIcon icon={faLink} className="mr-1" />
												Agon RPG
											</a>
										</li>
										<li>
											<strong>"Mareando el Carnero"</strong> - Es una partida completa y llena de humor que,
											adaptada al sistema DRAGON, se puede jugar en solo 2 horas, especialmente adecuada para
											aquellos que son nuevos en el mundo de los juegos de rol.
											<a
												href="/product/mareando-el-carnero"
												target="_blank"
												rel="noopener noreferrer"
												className="text-red-400 ml-2 hover:underline"
											>
												<FontAwesomeIcon icon={faLink} className="mr-1" />
												Detalles de la Aventura
											</a>
										</li>
									</ul>
								</div>
							</div>
						</div>
					</motion.div>

					{/* CTA Section */}
					<motion.div
						className="mt-16 text-center"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<h3 className="text-3xl font-amarante text-red-400 mb-6">
							¿Listo para tu primera aventura narrativa?
						</h3>
						<motion.a
							href={`https://api.whatsapp.com/send?phone=34642367972&text=${encodeURIComponent("Hola, estoy interesado/a en jugar DRAGON en Madrid. ¿Podéis ayudarme?")}`}
							className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-amarante text-xl transition-colors"
							whileHover={{
								scale: 1.1,
								boxShadow: "0 0 15px #f93d3d",
								backgroundColor: "white",
								color: "black",
								fontWeight: "900",
								fontStyle: "italic",
							}}
							whileTap={{ scale: 0.98 }}
							target="_blank"
							rel="noopener noreferrer"
						>
							Comenzar mi aventura
						</motion.a>
					</motion.div>
				</div>
			</section>
		</main>
	);
};

export default TarragonPage;
